import React from 'react';
import PropTypes from 'prop-types';
import { RadioGroup } from 'formik-material-ui';
import {
  Radio,
  FormControlLabel,
  makeStyles,
  useMediaQuery,
  FormHelperText,
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { getTranslation } from '../../../utils/functions';

const useInputStyles = (isMobileView, isError) =>
  makeStyles((theme) => ({
    wrapper: {
      position: 'relative',
    },
    formControl: {
      marginBottom: theme.spacing(5),
      marginLeft: 0,
      flexDirection: isMobileView ? 'column-reverse' : 'row-reverse',
    },
    label: {
      marginRight: theme.spacing(3),
      marginBottom: isMobileView ? theme.spacing(1) : 0,
      color: isError ? theme.palette.error.main : 'inherit',
    },
    helperText: {
      position: 'absolute',
      bottom: 15,
    },
  }));

const InputRadio = ({ hint, label, required, options, ...props }) => {
  const { t } = useTranslation();

  const optionsElements = options.map(({ key, value }) => (
    <FormControlLabel
      key={key}
      value={key}
      control={<Radio color="primary" required={required} />}
      label={getTranslation(value)}
    />
  ));

  const isMobileView = useMediaQuery((theme) => theme.breakpoints.down('xs'));

  const {
    field: { name },
    form: { errors, touched },
  } = props;

  const isError = touched[name] && errors?.[name];

  const classes = useInputStyles(isMobileView, isError)();

  const labelToDisplay = `${t(label)} ${required ? '*:' : ':'}`;

  const shouldDisplayError = () => touched[name] && !!errors[name];

  return (
    <div className={classes.wrapper}>
      <FormControlLabel
        classes={{
          label: classes.label,
          root: classes.formControl,
        }}
        component="fieldset"
        label={labelToDisplay}
        labelPlacement="start"
        control={
          <RadioGroup row={!isMobileView} {...props}>
            {optionsElements}
          </RadioGroup>
        }
      />
      <FormHelperText
        className={classes.helperText}
        variant="filled"
        required
        error={shouldDisplayError()}
        margin="dense"
      >
        {shouldDisplayError() ? errors[name] : hint}
      </FormHelperText>
    </div>
  );
};

InputRadio.propTypes = {
  label: PropTypes.string.isRequired,
  required: PropTypes.bool.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    }),
  ).isRequired,
  hint: PropTypes.string,
};

InputRadio.defaultProps = {
  hint: null,
};

export default InputRadio;
